import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X, Grid, List } from 'lucide-react';
import PropertyCard from '@/components/PropertyCard';
import Pagination from '@/components/Pagination';
import CurrencySwitcher from '@/components/CurrencySwitcher';
import { useCurrency } from '@/contexts/CurrencyContext';
import { prettifyAmenity } from '@/lib/amenityLabels';
import { getProperties } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const PAGE_SIZE = 10;

// Sale-property USD price thresholds — filter labels are formatted via active currency.
const MIN_PRICE_OPTIONS = [100000, 300000, 500000, 1000000];
const MAX_PRICE_OPTIONS = [300000, 500000, 1000000, 5000000];

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const { formatPriceShort } = useCurrency();
  
  // Get initial ownership type from URL or default to empty (show all)
  const initialOwnershipType = searchParams.get('ownership_type') || '';
  const [ownershipType, setOwnershipType] = useState(initialOwnershipType);
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    minPrice: searchParams.get('min_price') || '',
    maxPrice: searchParams.get('max_price') || '',
    propertyType: searchParams.get('property_type') || '',
    status: searchParams.get('status') || '',
    amenities: searchParams.get('amenities')?.split(',').filter(Boolean) || [],
    minLandSize: searchParams.get('min_land_size') || '',
    maxLandSize: searchParams.get('max_land_size') || '',
    minBuildingSize: searchParams.get('min_building_size') || '',
    maxBuildingSize: searchParams.get('max_building_size') || '',
    sort: searchParams.get('sort') || 'newest',
  });

  const locations = ['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud'];
  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'House' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'land', label: 'Land' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'boutique_hotel', label: 'Boutique Hotel' },
  ];
  const statusOptions = [
    { value: 'for_sale', label: 'For Sale' },
    { value: 'for_rent_long', label: 'For Rent (Monthly / Yearly)' },
    { value: 'for_rent_short', label: 'Managed by Lalita (Short Stay)' },
  ];
  const SIZE_OPTIONS = [100, 250, 500, 1000, 2000, 5000];
  const ownershipTypes = [
    { value: '', label: 'All' },
    { value: 'leasehold', label: 'Leasehold' },
    { value: 'freehold', label: 'Freehold' },
    { value: 'monthly_rental', label: 'Monthly Rental' },
    { value: 'yearly_rental', label: 'Yearly Rental' },
  ];
  const amenitiesOptions = [
    'private_pool',
    'garden',
    'ocean_view',
    'staff_included',
    'beach_access',
    'newly_built',
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'bedrooms', label: 'Bedrooms' },
  ];

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.location) params.location = filters.location;
        if (filters.bedrooms) params.bedrooms = filters.bedrooms;
        if (filters.minPrice) params.min_price = filters.minPrice;
        if (filters.maxPrice) params.max_price = filters.maxPrice;
        if (filters.propertyType) params.property_type = filters.propertyType;
        if (filters.status) params.status = filters.status;
        if (filters.amenities.length) params.amenities = filters.amenities.join(',');
        if (filters.minLandSize) params.min_land_size = filters.minLandSize;
        if (filters.maxLandSize) params.max_land_size = filters.maxLandSize;
        if (filters.minBuildingSize) params.min_building_size = filters.minBuildingSize;
        if (filters.maxBuildingSize) params.max_building_size = filters.maxBuildingSize;
        // Map synthetic ownership values to the right backend query
        if (ownershipType === 'monthly_rental') {
          params.rental_period = 'monthly';
        } else if (ownershipType === 'yearly_rental') {
          params.rental_period = 'yearly';
        } else if (ownershipType) {
          params.ownership_type = ownershipType;
        }
        params.sort = filters.sort;

        const res = await getProperties(params);
        setProperties(res.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Properties fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters, ownershipType]);

  // Reset to page 1 in local state whenever filters change. The URL `page`
  // param is removed inside the change handlers themselves so we don't trip
  // the stale-closure race that wipes the just-set filter param.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ownershipType]);

  const totalPages = Math.max(1, Math.ceil(properties.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProperties = useMemo(
    () => properties.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [properties, safePage]
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const handleOwnershipTypeChange = (type) => {
    setOwnershipType(type);
    // Clicking "All" (empty type) wipes ALL other filters too — matches the
    // user mental model that "All" means "show every property, no filters".
    if (!type) {
      setFilters({
        location: '',
        bedrooms: '',
        minPrice: '',
        maxPrice: '',
        propertyType: '',
        status: '',
        amenities: [],
        minLandSize: '',
        maxLandSize: '',
        minBuildingSize: '',
        maxBuildingSize: '',
        sort: 'newest',
      });
      setSearchParams({});
      return;
    }
    // Clear the Status filter — each ownership tab implies a specific status,
    // and any pre-existing Status filter (e.g. "For Sale") will conflict with
    // virtual rental tabs (monthly/yearly require status=for_rent_long).
    setFilters(prev => ({ ...prev, status: '' }));
    const params = new URLSearchParams(searchParams);
    params.set('ownership_type', type);
    params.delete('status');
    params.delete('page');
    setSearchParams(params);
  };

  // Map internal camelCase filter keys → URL snake_case keys
  const FILTER_URL_KEYS = {
    minPrice: 'min_price',
    maxPrice: 'max_price',
    propertyType: 'property_type',
    minLandSize: 'min_land_size',
    maxLandSize: 'max_land_size',
    minBuildingSize: 'min_building_size',
    maxBuildingSize: 'max_building_size',
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));

    const urlKey = FILTER_URL_KEYS[key] || key;
    const params = new URLSearchParams(searchParams);
    if (value) params.set(urlKey, value);
    else params.delete(urlKey);
    params.delete('page'); // reset pagination
    setSearchParams(params);
  };

  const handleAmenityToggle = (amenity) => {
    const newAmenities = filters.amenities.includes(amenity)
      ? filters.amenities.filter(a => a !== amenity)
      : [...filters.amenities, amenity];
    
    setFilters(prev => ({ ...prev, amenities: newAmenities }));
    
    const params = new URLSearchParams(searchParams);
    if (newAmenities.length) {
      params.set('amenities', newAmenities.join(','));
    } else {
      params.delete('amenities');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      bedrooms: '',
      minPrice: '',
      maxPrice: '',
      propertyType: '',
      status: '',
      amenities: [],
      minLandSize: '',
      maxLandSize: '',
      minBuildingSize: '',
      maxBuildingSize: '',
      sort: 'newest',
    });
    setSearchParams({});
  };

  const activeFilterCount = [
    filters.location,
    filters.bedrooms,
    filters.minPrice || filters.maxPrice,
    filters.propertyType,
    filters.status,
    filters.amenities.length > 0,
    filters.minLandSize || filters.maxLandSize,
    filters.minBuildingSize || filters.maxBuildingSize,
  ].filter(Boolean).length;

  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Location</label>
        <Select value={filters.location || "all"} onValueChange={(v) => handleFilterChange('location', v === "all" ? "" : v)}>
          <SelectTrigger className="w-full" data-testid="filter-location">
            <SelectValue placeholder="Any location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any location</SelectItem>
            {locations.map(loc => (
              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bedrooms */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Bedrooms</label>
        <Select value={filters.bedrooms || "all"} onValueChange={(v) => handleFilterChange('bedrooms', v === "all" ? "" : v)}>
          <SelectTrigger className="w-full" data-testid="filter-bedrooms">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="1">1 Bedroom</SelectItem>
            <SelectItem value="2">2 Bedrooms</SelectItem>
            <SelectItem value="3">3 Bedrooms</SelectItem>
            <SelectItem value="4">4+ Bedrooms</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Price Range</label>
        <div className="grid grid-cols-2 gap-2">
          <Select 
            value={filters.minPrice || "all"} 
            onValueChange={(v) => handleFilterChange('minPrice', v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-min-price">
              <SelectValue placeholder="Min" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Min</SelectItem>
              {MIN_PRICE_OPTIONS.map((v) => (
                <SelectItem key={v} value={String(v)}>{formatPriceShort(v)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={filters.maxPrice || "all"} 
            onValueChange={(v) => handleFilterChange('maxPrice', v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="filter-max-price">
              <SelectValue placeholder="Max" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Max</SelectItem>
              {MAX_PRICE_OPTIONS.map((v, idx) => (
                <SelectItem key={v} value={String(v)}>
                  {formatPriceShort(v)}{idx === MAX_PRICE_OPTIONS.length - 1 ? '+' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Property Type */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Property Type</label>
        <Select value={filters.propertyType || "all"} onValueChange={(v) => handleFilterChange('propertyType', v === "all" ? "" : v)}>
          <SelectTrigger className="w-full" data-testid="filter-property-type">
            <SelectValue placeholder="Any type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any type</SelectItem>
            {propertyTypes.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Status</label>
        <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange('status', v === "all" ? "" : v)}>
          <SelectTrigger className="w-full" data-testid="filter-status">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            {statusOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Land Size */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Land Size (m²)</label>
        <div className="grid grid-cols-2 gap-2">
          <Select value={filters.minLandSize || 'all'} onValueChange={(v) => handleFilterChange('minLandSize', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-min-land-size"><SelectValue placeholder="Min" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Min</SelectItem>
              {SIZE_OPTIONS.map((s) => (
                <SelectItem key={s} value={String(s)}>{s.toLocaleString()} m²</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.maxLandSize || 'all'} onValueChange={(v) => handleFilterChange('maxLandSize', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-max-land-size"><SelectValue placeholder="Max" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Max</SelectItem>
              {SIZE_OPTIONS.map((s, i) => (
                <SelectItem key={s} value={String(s)}>{s.toLocaleString()} m²{i === SIZE_OPTIONS.length - 1 ? '+' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Building Size */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">Building Size (m²)</label>
        <div className="grid grid-cols-2 gap-2">
          <Select value={filters.minBuildingSize || 'all'} onValueChange={(v) => handleFilterChange('minBuildingSize', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-min-building-size"><SelectValue placeholder="Min" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Min</SelectItem>
              {SIZE_OPTIONS.map((s) => (
                <SelectItem key={s} value={String(s)}>{s.toLocaleString()} m²</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.maxBuildingSize || 'all'} onValueChange={(v) => handleFilterChange('maxBuildingSize', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-max-building-size"><SelectValue placeholder="Max" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Max</SelectItem>
              {SIZE_OPTIONS.map((s, i) => (
                <SelectItem key={s} value={String(s)}>{s.toLocaleString()} m²{i === SIZE_OPTIONS.length - 1 ? '+' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Amenities */}
      <div>
        <label className="block text-sm font-medium text-charcoal mb-3">Amenities</label>
        <div className="space-y-3">
          {amenitiesOptions.map(amenity => (
            <label key={amenity} className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={filters.amenities.includes(amenity)}
                onCheckedChange={() => handleAmenityToggle(amenity)}
                data-testid={`filter-amenity-${amenity}`}
              />
              <span className="text-sm text-charcoal">{prettifyAmenity(amenity)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full"
          data-testid="clear-filters-btn"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-warm-sand pt-24 pb-16" data-testid="properties-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-2">
            Browse Properties
          </h1>
          <p className="font-body text-charcoal-light">
            {loading ? 'Loading...' : `${properties.length} properties found`}
            {filters.location && ` in ${filters.location}`}
          </p>
        </motion.div>

        {/* Ownership / Rental Quick Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
            <div className="flex flex-wrap gap-2">
              {ownershipTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => handleOwnershipTypeChange(type.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    ownershipType === type.value
                      ? 'bg-charcoal text-white'
                      : 'bg-white text-charcoal hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid={`ownership-${type.value || 'all'}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </motion.div>

        <div className="flex gap-8">
          {/* Desktop Filters */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden lg:block w-72 flex-shrink-0"
          >
            <div className="bg-white rounded-2xl p-6 shadow-card sticky top-28">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-medium text-charcoal">Filters</h3>
                {activeFilterCount > 0 && (
                  <span className="bg-ubud-jungle text-white text-xs px-2 py-1 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <FilterPanel />
            </div>
          </motion.aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filters & Sort */}
            <div className="flex items-center justify-between gap-4 mb-6">
              {/* Mobile Filter Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-2 bg-ubud-jungle text-white text-xs px-2 py-0.5 rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterPanel />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort & Currency */}
              <div className="flex items-center gap-3 ml-auto">
                <CurrencySwitcher variant="page" className="hidden sm:inline-flex" />
                <span className="text-sm text-charcoal-muted hidden sm:inline">Sort by:</span>
                <Select value={filters.sort} onValueChange={(v) => handleFilterChange('sort', v)}>
                  <SelectTrigger className="w-40" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Properties Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <p className="text-charcoal-light text-lg mb-4">No properties found matching your criteria.</p>
                <Button onClick={clearFilters} variant="outline">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {paginatedProperties.map((property, idx) => {
                    // When filtering by a specific rental period, force the
                    // card to display THAT price so Yearly Rental shows
                    // yearly price (not monthly fallback).
                    const cardPriceType =
                      ownershipType === 'yearly_rental' ? 'yearly' :
                      ownershipType === 'monthly_rental' ? 'monthly' : null;
                    return (
                      <PropertyCard key={property.id} property={property} index={idx} priceType={cardPriceType} />
                    );
                  })}
                </div>
                <Pagination
                  currentPage={safePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  testIdPrefix="properties-pagination"
                />
                {totalPages > 1 && (
                  <p className="text-center text-sm text-charcoal-muted mt-3" data-testid="properties-page-info">
                    Page {safePage} of {totalPages} · {properties.length} total
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
